
import { defineComponent } from 'vue'
import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk'
import KycService from '@/services/kyc.service'
import { useAccountStore } from '@/store/auth/account.store'

export default defineComponent({
  name: 'VeriffVerification',
  props: {
    clientId: {
      type: Number,
      required: true
    },
    autoStart: {
      type: Boolean,
      default: false
    },
    existingSessionUrl: {
      type: String,
      default: null
    }
  },
  data: () => ({
    loading: false,
    showError: false,
    errorMessage: '',
    accountStore: useAccountStore()
  }),
  mounted() {
    if (this.autoStart) {
      this.startVerification()
    }
  },
  methods: {
    async startVerification() {
      this.loading = true
      this.showError = false

      try {
        let veriffSessionUrl = this.existingSessionUrl
        const kycStatus = this.accountStore.accountData.kycVerificationStatus

        // Si le statut est NOT_VERIFIED ou si aucune session existante, créer une nouvelle session
        if (!veriffSessionUrl || kycStatus === 'NOT_VERIFIED') {
          console.log('Création d\'une nouvelle session Veriff (statut:', kycStatus, ')')
          const kycInfo = await KycService.getClientKycIntegrationInfo(this.clientId, true)

          // Vérifier si une session Veriff a été créée
          if (!kycInfo.veriffSessionUrl) {
            throw new Error((this.$t('noVeriffSession') as string))
          }

          veriffSessionUrl = kycInfo.veriffSessionUrl
          console.log('Nouvelle session Veriff créée')
        } else {
          console.log('Utilisation de la session Veriff existante')
        }

        // Toujours ouvrir le modal Veriff si on a une URL de session
        // Peu importe si la vérification est pending ou non
        // L'utilisateur doit pouvoir continuer/compléter sa vérification

        // Désactiver le loading pour permettre l'ouverture du modal
        this.loading = false

        // Attendre un court instant pour que l'UI soit prête
        await new Promise(resolve => setTimeout(resolve, 100))

        console.log('Ouverture du modal Veriff avec l\'URL:', veriffSessionUrl)

        // Créer et ouvrir le frame Veriff
        // createVeriffFrame ouvre automatiquement le modal
        createVeriffFrame({
          url: veriffSessionUrl,
          onEvent: (msg: string) => {
            this.handleVeriffEvent(msg)
          }
        })

        // Émettre l'événement de démarrage
        this.$emit('verification-started')
      } catch (error: any) {
        console.error('Erreur lors du démarrage de la vérification:', error)
        this.errorMessage = error.message || (this.$t('verificationError') as string)
        this.showError = true
        this.loading = false
      }
    },

    async handleVeriffEvent(message: string) {
      console.log('Veriff event:', message)

      switch (message) {
        case MESSAGES.STARTED:
          // Vérification démarrée - l'utilisateur a commencé le processus
          console.log('Vérification Veriff démarrée par l\'utilisateur')
          break

        case MESSAGES.FINISHED:
          // Vérification terminée avec succès - l'utilisateur a soumis ses documents
          console.log('Vérification Veriff terminée')
          this.$emit('verification-finished')
          await this.refreshUserData()
          break

        case MESSAGES.CANCELED:
          // Vérification annulée par l'utilisateur
          console.log('Vérification Veriff annulée')
          this.$emit('verification-canceled')
          break

        default:
          console.log('Événement Veriff inconnu:', message)
      }
    },

    async refreshUserData() {
      try {
        // Attendre quelques secondes pour que le backend traite le webhook
        await new Promise(resolve => setTimeout(resolve, 3000))

        // Rafraîchir les données utilisateur
        await this.accountStore.refreshData()

        // Vérifier le nouveau statut KYC
        const isVerified =
          this.accountStore.accountData.kycVerificationStatus === 'VERIFIED' ||
          this.accountStore.accountData.kycVerificationStatus === 'APPROVED' ||
          this.accountStore.accountData.kycVerificationStatus === 'COMPLETED'

        if (isVerified) {
          this.$emit('verification-success')
        }
      } catch (error) {
        console.error('Erreur lors du rafraîchissement des données:', error)
      }
    }
  }
})
